import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  OnDestroy,
  HostBinding, HostListener
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { getImageFitBounds, getMarkerPosition } from "../../../shared/utils/images";

@Component({
    selector: 'app-zoomable-image',
    templateUrl: './zoomable-image.component.html',
    styleUrls: ['./zoomable-image.component.scss'],
    standalone: false
})
export class ZoomableImageComponent implements OnChanges, OnDestroy {
  @ViewChild('img') img: ElementRef<HTMLImageElement>;
  @Input() src: string;
  @Input() at: number;
  @Input() flagged: boolean;
  @Input() zoom: boolean;
  @Input() brightness: number;
  @Input() contrast: number;
  @Input() highlightUnseen: boolean;
  @Input() variant: 'single' | 'multiple' = 'single';
  @Input() position: { percentX: number; percentY: number };

  @HostBinding('class.style-single-image')
  get styleA() {
    return this.variant === 'single';
  }

  @HostBinding('class.style-multiple-image')
  get styleB() {
    return this.variant === 'multiple';
  }

  @HostListener('window:resize')
  onResize() {
    this.updateMarkerPosition();
  }

  @Output() loadedIn = new EventEmitter<number>();
  @Output() flaggedSeenFor = new EventEmitter<{ at: number; time: number }>();
  @Output() audit = new EventEmitter<{ percentX: number; percentY: number }>();

  loading$ = new BehaviorSubject<boolean>(false);
  showLoader$ = this.loading$.pipe(debounceTime(100));
  zooming = false;
  loadStartTime: number;
  flaggedStartTime: number;
  markerLeft = 0;
  markerTop = 0;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('zoom' in changes && !changes.zoom.currentValue) {
      this.stopZooming();
    }
    if ('at' in changes && changes.at.previousValue) {
      this.emitSeenTime(changes.at.previousValue);
    } else if ('highlightUnseen' in changes || this.highlightUnseen) {
      this.emitSeenTime(this.at);
    }
    if ('src' in changes && changes.src.currentValue) {
      this.loadStartTime = Date.now();
      this.loading$.next(true);
      const img = new Image();
      img.onload = () => {
        this.loadedIn.emit(Date.now() - this.loadStartTime);
        this.loading$.next(false);
        if (this.flagged) this.flaggedStartTime = Date.now();
        if (this.flagged && this.highlightUnseen) this.emitSeenTime(this.at);
      };
      img.src = changes.src.currentValue;
    }
    this.updateMarkerPosition();
  }

  emitSeenTime(at: number) {
    if (!this.flaggedStartTime) return;
    this.flaggedSeenFor.emit({
      at: at,
      time: Date.now() - this.flaggedStartTime,
    });
    this.flaggedStartTime = 0;
  }

  ngOnDestroy(): void {
    if (this.flaggedStartTime > 0)
      this.flaggedSeenFor.emit({
        at: this.at,
        time: Date.now() - this.flaggedStartTime,
      });
  }

  onMouseOver(event: MouseEvent) {
    if (!this.zoom || this.loading$.value) return;
    this.startZooming(event.offsetX, event.offsetY);
  }

  onMouseLeave() {
    if (!this.zoom) return;
    this.stopZooming();
  }

  onMouseMove(event: MouseEvent) {
    if (!this.zoom || this.loading$.value) return;
    if (!this.zooming) {
      this.startZooming(event.offsetX, event.offsetY);
    } else {
      this.moveBackground(event.offsetX, event.offsetY);
    }
  }

  startZooming(x: number, y: number) {
    const img = this.img.nativeElement as HTMLImageElement;

    if (img.width > img.naturalWidth && img.height > img.naturalHeight) return;

    img.style.objectFit = 'none';
    this.moveBackground(x, y);
    this.zooming = true;
  }

  stopZooming() {
    if (!this.img) return;
    const img = this.img.nativeElement as HTMLImageElement;
    img.style.objectFit = 'contain';
    img.style.objectPosition = `center`;
    this.zooming = false;
  }

  moveBackground(x: number, y: number) {
    const img = this.img.nativeElement as HTMLImageElement;

    const xp = x / img.width,
      yp = y / img.height;

    const nx = Math.min(Math.max(0, xp * img.naturalWidth - img.width / 2), img.naturalWidth - img.width);
    const ny = Math.min(Math.max(0, yp * img.naturalHeight - img.height / 2), img.naturalHeight - img.height);

    img.style.objectPosition = `${-nx}px ${-ny}px`;
  }

  sanitizeStyle(unsafeStyle: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(unsafeStyle);
  }

  imageClicked($event: MouseEvent) {
    const img = this.img.nativeElement as HTMLImageElement;
    const rect = img.getBoundingClientRect();

    const x = $event.clientX - rect.left;
    const y = $event.clientY - rect.top;

    const { offsetX, offsetY, displayedWidth, displayedHeight } = getImageFitBounds(img, rect.width, rect.height);

    const percentX = Math.min(1, Math.max(0, (x - offsetX) / displayedWidth));
    const percentY = Math.min(1, Math.max(0, (y - offsetY) / displayedHeight));

    return this.audit.emit({ percentX, percentY });
  }

  updateMarkerPosition(): void {
    if (!this.position || !this.img?.nativeElement) {
      this.markerLeft = 0;
      this.markerTop = 0;
      return;
    }

    this.markerLeft = getMarkerPosition(this.img.nativeElement, this.position).left;
    this.markerTop = getMarkerPosition(this.img.nativeElement, this.position).top;
  }
}
